import { Component, ChangeDetectionStrategy, inject, signal, input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {finalize} from 'rxjs/operators';
import {ProjectService} from '@app/services/project.service';
import {GlobalWidgetService} from '@app/services/global-widget.service';
import {
  WidgetCustomizationAiContext,
  WidgetCustomizationAiMode,
  WidgetCustomizationAiRequest,
  WidgetCustomizationAiResult
} from './widget-customization-ai.types';
import { AceEditorComponent } from '../ace-editor/ace-editor.component';

@Component({
  selector: 'app-widget-customization-ai-modal',
  templateUrl: './widget-customization-ai-modal.component.html',
  styleUrls: ['./widget-customization-ai-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, AceEditorComponent]
})
export class WidgetCustomizationAiModalComponent {
  activeModal = inject(NgbActiveModal);
  private formBuilder = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private globalWidgetService = inject(GlobalWidgetService);

  readonly context = input<WidgetCustomizationAiContext>('project');
  readonly projectId = input<number>(undefined);
  readonly registrationPlatformId = input<number>(null);
  readonly currentScript = input('');
  readonly currentCss = input('');
  readonly hasLegacyCustomization = input(false);
  form: FormGroup;
  readonly loading = signal(false);
  readonly error = signal('');
  readonly result = signal<WidgetCustomizationAiResult>(null);
  aceEditorOptions: any = {maxLines: 30, minLines: 12};

  constructor() {
    this.form = this.formBuilder.group({
      mode: ['fresh', Validators.required],
      prompt: ['', [Validators.required, Validators.maxLength(4000)]]
    });
    this.form.get('mode').valueChanges.subscribe((mode: WidgetCustomizationAiMode) => {
      this.updatePromptValidators(mode);
    });
    this.form.valueChanges.subscribe(() => {
      if (!this.loading()) {
        this.error.set('');
      }
    });
  }

  initialize(): void {
    this.form.patchValue({mode: 'fresh', prompt: ''});
  }

  private updatePromptValidators(mode: WidgetCustomizationAiMode): void {
    const promptControl = this.form.get('prompt');
    if (mode === 'convert_legacy') {
      promptControl.clearValidators();
    } else {
      promptControl.setValidators([Validators.required, Validators.maxLength(4000)]);
    }
    promptControl.updateValueAndValidity();
  }

  get isLegacyConversion(): boolean {
    return this.form.get('mode').value === 'convert_legacy';
  }

  convertLegacy(): void {
    this.form.get('mode').setValue('convert_legacy');
    this.generate();
  }

  generate(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.loading()) {
      return;
    }

    const mode = this.form.get('mode').value as WidgetCustomizationAiMode;
    const payload: WidgetCustomizationAiRequest = {
      mode,
      prompt: this.form.get('prompt').value || ''
    };
    if (mode === 'modify') {
      payload.current = {
        custom_script: this.currentScript() || '',
        custom_css: this.currentCss() || ''
      };
    }
    const registrationPlatformId = this.registrationPlatformId();
    if (registrationPlatformId) {
      payload.registration_platform_id = registrationPlatformId;
    }

    this.result.set(null);
    this.loading.set(true);
    this.error.set('');
    const request = this.context() === 'global-widget'
      ? this.globalWidgetService.generateWidgetCustomization(payload)
      : this.projectService.generateWidgetCustomization(this.projectId(), payload);
    request
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.result.set(response.data);
        },
        error: (error) => {
          this.error.set(this.getGenerationErrorMessage(error));
        }
      });
  }

  private getGenerationErrorMessage(error: any): string {
    const status = Number(error?.status || 0);
    const response = error?.error;
    const publicMessage = this.readErrorMessage(response?.error)
      || this.readErrorMessage(response?.publicError);

    if (status === 0) {
      return 'Could not connect to the AI service. Check your connection and try again.';
    }
    if (status === 401) {
      return 'Your session has expired. Refresh the page, sign in, and try again.';
    }
    if (status === 403) {
      return 'You do not have permission to generate widget customization.';
    }
    if (status === 408 || status === 504) {
      return 'The AI service took too long to respond. Please try again.';
    }
    if (status === 429) {
      return 'The AI service is receiving too many requests. Wait a moment and try again.';
    }
    if (publicMessage) {
      return publicMessage;
    }
    if (status === 502 || status === 503) {
      return 'The AI customization service is temporarily unavailable. Please try again shortly.';
    }

    const responseMessage = this.readErrorMessage(response);
    if (status === 400 || status === 422) {
      return this.getValidationErrorMessage(responseMessage);
    }
    return 'Could not generate the customization. Please try again.';
  }

  private readErrorMessage(value: any): string {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value?.message === 'string') {
      return value.message.trim();
    }
    return '';
  }

  private getValidationErrorMessage(message: string): string {
    if (/generated custom script syntax error/i.test(message)) {
      return 'The AI generated invalid JavaScript. Please try again or simplify your request.';
    }
    if (/generated custom css syntax error/i.test(message)) {
      return 'The AI generated invalid CSS. Please try again or simplify your request.';
    }
    if (/prompt is required/i.test(message)) {
      return 'Describe the widget customization you want to generate.';
    }
    if (/prompt must not exceed/i.test(message)) {
      return 'Your description is too long. Shorten it to 4,000 characters and try again.';
    }
    if (/no legacy widget customization/i.test(message)) {
      return 'There is no legacy widget customization to convert.';
    }
    if (/current\.(custom_script|custom_css)/i.test(message)) {
      return 'The current widget code could not be read. Close this window and try again.';
    }
    if (/registration platform not found/i.test(message)) {
      return 'The selected registration platform is no longer available. Refresh the page and try again.';
    }
    if (/project not found/i.test(message)) {
      return 'This project is no longer available. Refresh the page and try again.';
    }
    if (/^mode must be/i.test(message)) {
      return 'Select a valid generation mode and try again.';
    }
    return 'The request could not be processed. Review your prompt and try again.';
  }

  apply(): void {
    const result = this.result();
    if (result) {
      this.activeModal.close(result);
    }
  }
}
